// @flow

import React, { Fragment } from 'react'
import { StickyContainer, Sticky } from 'react-sticky'
import { withRouter } from 'react-router-dom'
import { Visibility } from 'semantic-ui-react'
import { ownerName } from 'frontend_core/dist/helpers/format'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AttentionPopup from 'components/organisms/room/AttentionPopup'
import { setUIState, containerIds } from 'redux/modules/ui'
import { actions as roomActions } from 'redux/modules/room'
import { actions as favoriteActions } from 'redux/modules/favorite'
import MainTemplate from 'components/templates/MainTemplate'
import { roomName, roomImageURL } from 'frontend_core/dist/helpers/format'
import { isMobile } from 'common/screen'
import Breadcrumbs from 'components/shared/Breadcrumbs'
import Path from 'routes/path'
import { imagePath } from 'frontend_core/dist/helpers/image'
import {
  convertToSearchSessionIdParams,
  getUserAvailablePoints,
} from 'helpers/search'

import AddFavoriteSp from 'components/molecules/room/AddFavoriteSp'
import BottomRequest from 'components/molecules/room/BottomRequest'

import DetailHeader from 'components/organisms/room/DetailHeader'
import CoverPhoto from 'components/organisms/room/CoverPhoto'
import CoverPhotoMobile from 'containers/organisms/room/CoverPhotoMobile'
import CoverPhotoSlideShow from 'containers/organisms/room/CoverPhotoSlideShow'
import DetailHeadnoteSection from 'components/organisms/room/DetailHeadnoteSection'
import DetailSponsorSection from 'components/organisms/room/DetailSponsorSection'
import DetailUsageSection from 'components/organisms/room/DetailUsageSection'
import DetailAccessSection from 'components/organisms/room/DetailAccessSection'
import DetailAccessSectionSp from 'components/organisms/room/DetailAccessSectionSp'
import DetailMainSection from 'components/organisms/room/DetailMainSection'
import DetailPlanSection from 'components/organisms/room/DetailPlanSection'
import PlanOptionList from 'components/organisms/room/PlanOptionList'
import DetailUsecaseSection from 'components/organisms/room/DetailUsecaseSection'
import DetailMediaSection from 'components/organisms/room/DetailMediaSection'
import DetailAmenities from 'components/organisms/room/DetailAmenities'
import DetailOwnerSection from 'components/organisms/room/DetailOwnerSection'
import DetailSideBox from 'components/organisms/room/DetailSideBox'
import DetailSideBoxSp from 'components/organisms/room/DetailSideBoxSp'
import DetailAttentionSection from 'components/organisms/room/DetailAttentionSection'
import MySelfRoomBanner from 'components/organisms/room/MySelfRoomBanner'
import DetailReviewSection from 'components/organisms/room/DetailReviewSection'
import FavoriteSelectModal from 'containers/organisms/FavoriteSelectModal'
import NeighborhoodRoomsSlider from 'containers/organisms/room/RoomCardSlider/NeighborhoodRooms'
import BrowseRoomHistoriesSlider from 'containers/organisms/room/RoomCardSlider/BrowseRoomHistories'
import InquiryModal from 'containers/organisms/room/InquiryModal'
import UserContactModal from 'containers/organisms/UserContactModal'

import graphql from './graphql'
import RoomUseCase, { type RoomUseCaseType } from './usecase'
import RoomPresenter from './RoomPagePresenter'

function PlaneView({ renderContent }) {
  return renderContent()
}

const STICKY_BOTTOM_THRESHOLD = 450 // memo Stickyの下限を調整する場合はここを修正してください
const STICKY_TOP_THRESHOLD = 24 // memo Stickyの上限を調整する場合はここを修正してください
function SideView({ renderContent }) {
  return (
    <Sticky>
      {params => {
        const style = {}
        if (params.distanceFromBottom < STICKY_BOTTOM_THRESHOLD) {
          style.top = params.distanceFromBottom - STICKY_BOTTOM_THRESHOLD
        } else {
          style.top = STICKY_TOP_THRESHOLD
        }
        return renderContent({ ...params.style, ...style })
      }}
    </Sticky>
  )
}

export type PropTypes = {
  data: {
    room: Object,
    amenities: {
      results: Array<{
        name: string,
        nameText: string,
      }>,
    },
    promotions: Object,
    viewer: Object,
  },
  ui: Object,
  actions: {
    setUIState: Function,
  },
  config: {
    SPM_GOOGLE_API_KEY: string,
    SERVICE: string,
    URL: string,
    ORIGIN: string,
  },
  room: {
    roomDetail: {
      isFavorite: boolean,
    },
  },
  staticContext: Object,
  usecase: RoomUseCaseType,
  location: {
    pathname: string,
  },
}

class RoomPage extends React.Component<*, PropTypes, *> {
  constructor(props: PropTypes) {
    super(props)

    const { usecase, data } = this.props

    usecase.initialize()

    this.roomPresenter = new RoomPresenter(data.room)
  }

  roomPresenter: RoomPresenter

  componentDidMount() {
    const { usecase, data } = this.props
    usecase.checkAttention()
    usecase.setRoom()

    window.addEventListener(
      'resize',
      () => {
        this.forceUpdate()
      },
      false,
    )

    this.roomPresenter.setRoom(data.room)
  }

  componentWillUnmount() {
    window.removeEventListener('resize')
  }

  // 先週◯人が使いましたポップアップ表示
  renderAttensionPopup() {
    if (this.props.ui.isAttentionPopupOpen === false) {
      return null
    }

    return (
      <AttentionPopup
        guestsCount={this.props.data.room.usedGuestsCount}
        onClickClose={e => {
          e.preventDefault()
          this.props.actions.setUIState(containerIds.ROOM, {
            isAttentionPopupOpen: false,
          })
        }}
      />
    )
  }

  render() {
    const { ui, data, actions, usecase, location, config } = this.props

    // ルームが見つからない時は404
    if (
      !data ||
      !data.room ||
      (!data.room.allowRentTypes.includes('DAY_TIME') &&
        !data.room.allowRentTypes.includes('NONE'))
    ) {
      this.props.staticContext.status = 404
      return null
    }

    const { room, amenities, viewer } = data
    const space = room.space
    const owner = room.owner

    const StickyView = isMobile() ? PlaneView : SideView

    const owners = []
    if (viewer) {
      owners.push(...viewer.owners.results.map(usersOwner => usersOwner.id))
    }

    const searchSessionIdParams = convertToSearchSessionIdParams(
      location.search,
    )
    const searchSessionId = searchSessionIdParams
      ? searchSessionIdParams.searchSessionId
      : null

    // thumbnailsとoptionItems.thumbnailsを合わせてスライドに表示する
    const thumbnails = this.roomPresenter.getThumbnails()

    return (
      <MainTemplate
        title={roomName(room)}
        description={room.description}
        image={roomImageURL(room) || imagePath('www/common/ogimage.jpg')}
        location={location}
        {...this.props}
        addtionalComponent={
          owners.includes(room.owner.id) && (
            <MySelfRoomBanner
              dashboardUrl={Path.editRoom(owner.username, room)}
              hidden={room.roomSettingStatus === 0}
            />
          )
        }
        linkCanonical={`${config.ORIGIN}${location.pathname}`}
      >
        <Breadcrumbs breadcrumbs={room.breadcrumbs} />

        <div
          className="site-main"
          itemScope
          itemType="http://schema.org/LodgingBusiness"
        >
          {!isMobile() && (
            <CoverPhoto
              src={
                Array.isArray(thumbnails) && thumbnails.length > 0
                  ? thumbnails[0].url
                  : imagePath('common/noimage_search.png')
              }
              onClick={e => usecase.displayAtSlideShow(e, 0)}
            />
          )}
          <div className="space-detail__mainv">
            <CoverPhotoMobile {...this.props} />
          </div>
          <CoverPhotoSlideShow {...this.props} />
          <StickyContainer>
            <div className="space-detail">
              <div className="wrapper">
                <div className="contents">
                  <div className="row">
                    <AddFavoriteSp
                      favorited={this.props.room.roomDetail.isFavorite}
                      onClick={(e: Event) =>
                        usecase.addFavorite(e, this.props.room.roomDetail)
                      }
                      showingFavoriteListTooltip={
                        isMobile() && ui.isOpenFavoriteListTooltip
                      }
                      addedFavoriteListName={ui.addedFavoriteListName}
                    />
                    <div className="space-detail__head">
                      <DetailHeader
                        name={room.name}
                        stateUrl={Path.area(`${space.state}/${space.city}`)}
                        stateText={`${space.stateText}${space.city}`}
                        spaceTypeUrl={Path.spaceType(space.spaceType)}
                        spaceTypeText={space.spaceTypeText}
                        capacityUrl={Path.capacity(room.capacity)}
                        capacity={room.capacity}
                        reputationScore={room.reputationSummary.score}
                        reputationsCount={room.reputationSummary.count}
                        reputationsUrl={Path.roomReputationList(
                          space.username,
                          room.uid,
                        )}
                      />
                    </div>
                    <div className="side-fixed">
                      <StickyView
                        renderContent={(style = {}) => (
                          <div style={isMobile() ? null : style}>
                            <DetailSideBox
                              data={data}
                              onClickFavorite={(e: Event) =>
                                usecase.addFavorite(
                                  e,
                                  this.props.room.roomDetail,
                                )
                              }
                              onClickShare={usecase.toggleShare}
                              favoritesCount={room.favoritesCount}
                              favorited={this.props.room.roomDetail.isFavorite}
                              showingFavoriteListTooltip={
                                ui.isOpenFavoriteListTooltip
                              }
                              addedFavoriteListName={ui.addedFavoriteListName}
                              showingShare={ui.isShowingShare}
                              searchSessionId={searchSessionId}
                              availablePoint={getUserAvailablePoints(viewer)}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-main page__space-detail">
                    <DetailHeadnoteSection
                      area={room.area}
                      capacity={room.capacity}
                    />

                    <DetailSponsorSection
                      campaigns={room.sponsoredPromotions.results}
                    />

                    <DetailAccessSectionSp
                      name={space.name}
                      state={space.stateText}
                      city={space.city}
                      address1={space.address1}
                      address2={space.address2}
                      access={space.access}
                      latitude={space.latitude}
                      longitude={space.longitude}
                      googleMapReactBootstrapURLKeys={{
                        key: this.props.config.SPM_GOOGLE_API_KEY,
                      }}
                    />

                    <DetailMainSection
                      sectionTitle={i18n.t('page.room.facility')}
                      isOpen={ui.isFacilityOpen}
                      onClickReadmore={() =>
                        actions.setUIState(containerIds.ROOM, {
                          isFacilityOpen: true,
                        })
                      }
                    >
                      <p>{room.description}</p>
                    </DetailMainSection>

                    <DetailMainSection
                      sectionTitle={i18n.t('page.room.service')}
                      isOpen={ui.isServiceOpen}
                      onClickReadmore={() =>
                        actions.setUIState(containerIds.ROOM, {
                          isServiceOpen: true,
                        })
                      }
                    >
                      <p>{room.equipmentDescription}</p>
                    </DetailMainSection>

                    <DetailUsecaseSection
                      entry={room.relatedEntries.results[0]}
                    />

                    <DetailMediaSection
                      photos={thumbnails}
                      onClickPhoto={usecase.displayAtSlideShow}
                      videoUrl={room.embedVideoUrl}
                      vrUrl={room.embedVrUrl}
                    />

                    <DetailUsageSection rates={room.usageRates} />

                    <DetailSideBoxSp
                      data={data}
                      onClickFavorite={(e: Event) =>
                        usecase.addFavorite(e, this.props.room.roomDetail)
                      }
                      onClickShare={usecase.toggleShare}
                      favoritesCount={room.favoritesCount}
                      favorited={this.props.room.roomDetail.isFavorite}
                      showingFavoriteListTooltip={ui.isOpenFavoriteListTooltip}
                      showingShare={ui.isShowingShare}
                      addedFavoriteListName={ui.addedFavoriteListName}
                    />
                    <DetailAccessSection
                      name={space.name}
                      state={space.stateText}
                      city={space.city}
                      address1={space.address1}
                      address2={space.address2}
                      access={space.access}
                      latitude={space.latitude}
                      longitude={space.longitude}
                      googleMapReactBootstrapURLKeys={{
                        key: this.props.config.SPM_GOOGLE_API_KEY,
                      }}
                    />
                  </div>

                  <div className="space-main">
                    <DetailPlanSection
                      room={room}
                      todayOK={room.reservableStart === 0}
                      plans={room.plans.results}
                      searchSessionId={searchSessionId}
                      optionList={
                        <Fragment>
                          <Visibility
                            onBottomPassed={usecase.displayBottomRequestButton}
                            onBottomPassedReverse={
                              usecase.hideBottomRequestButton
                            }
                            continuous
                          />
                          <PlanOptionList
                            open={ui.isOpenOptionList}
                            options={room.optionItems.results}
                            onClickMoreView={usecase.openOptionItem}
                          />
                        </Fragment>
                      }
                    />

                    {/* 下部固定リクエストボタン */}
                    <BottomRequest
                      display={ui.isDisplayBottomRequestButtom}
                      requestUrl={Path.reservationRequest(
                        room.space.username,
                        room.uid,
                        null,
                        searchSessionId,
                      )}
                    />

                    {/* アメニティ */}
                    <DetailAmenities
                      isOpen={ui.isAmenityOpen}
                      onClickReadmore={usecase.openAmenity}
                      masterAmenities={amenities.results}
                      roomAmenities={room.amenities.results}
                    />

                    {/* レビュー */}
                    <DetailReviewSection
                      spaceUsername={space.username}
                      roomUid={room.uid}
                      score={room.reputationSummary.score}
                      count={room.reputationSummary.count}
                      owner={room.owner}
                      reputations={room.reputations.results}
                    />

                    {/* このスペースの管理者 */}
                    <DetailOwnerSection
                      name={ownerName(owner)}
                      profile={owner.profile}
                      ownerPath={Path.owner(owner.username)}
                      ownerProfileImage={owner.profileImage}
                      isTopHost={owner.rank === 1}
                      createdAt={owner.createdAt}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="page__even space-main__attention">
              <div className="wrapper">
                <div className="contents">
                  <DetailAttentionSection
                    room={room}
                    onClickInquiry={usecase.openInquiryModal}
                  />
                </div>
              </div>
            </div>
          </StickyContainer>
          <div className="wrapper space-main__section-big space-main__otherspace">
            <div className="contents">
              <NeighborhoodRoomsSlider currentUser={viewer} />
              <BrowseRoomHistoriesSlider currentUser={viewer} />
            </div>
          </div>
        </div>
        {this.renderAttensionPopup()}
        <Visibility
          onBottomPassed={usecase.hideBottomRequestButton}
          continuous
        />
        {ui.isOpenFavoriteSelectModal && (
          <FavoriteSelectModal
            roomId={ui.room.id}
            roomUid={ui.room.uid}
            currentUser={viewer}
          />
        )}
        {ui.isOpenInquiry && <InquiryModal room={room} />}
        {ui.isOpenUserContact && (
          <UserContactModal currentUser={viewer || {}} fromType="inquiry" />
        )}
      </MainTemplate>
    )
  }
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = { setUIState, ...roomActions, ...favoriteActions }
  return { actions: bindActionCreators(bindActions, dispatch) }
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(graphql(RoomUseCase(RoomPage))),
)



// WEBPACK FOOTER //
// ./src/containers/pages/RoomPage/index.jsx